import { faAdd, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import ChaseRenderRow from "../components/chaseListComponents/chaseRenderRow";
import { chaseHeadingData } from "../components/chaseListComponents/chaseListData";
import { useEffect, useRef, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import TableWrapper, { tableHeaderClass } from "../components/TableWrapper";
import { useDebounceValue } from "usehooks-ts";
import formQueryUrl from "../utils/formQueryUrl";
import PaginationMenu from "../components/pagination/paginationMenu";
import GetUser from "../utils/getUser";
import TopBarWidget from "../components/TopDashboardWidget";
import { ParseErrors } from "../utils/ParseErrors";
import ChatIcon from "../components/chat-components/chat-icon";
import { useChaseMenu } from "../store/use-chase-menu";
import CustomerSearch from "../components/chaseListComponents/customer-search";
import ChaseFilterBar from "../components/chaseListComponents/chase-filter-bar";

const ChaseList = () => {
  const currentUser = GetUser();
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const location = window.location.pathname;
  const previousLocation = useRef(location);
  const myCutomers =
    window.location.pathname === "/my-customers" ||
    window.location.pathname === "/customer-archives";
  const mycustomer = window.location.pathname === "/my-customers";
  const customerArchive = window.location.pathname === "/customer-archives";
  const chaseStatus = window.location.pathname.split("/")[2] || "";
  const isSearchPage = window.location.pathname === "/chaselist";
  const { onToggleChaseMenu, onChaseMenuClose } = useChaseMenu();
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [query, setQuery] = useDebounceValue("", 1000);
  // search and page only count on the route they were set on, so a route change never sends a stale search
  const [listPath, setListPath] = useState<string>(location);
  const activeQuery = listPath === location ? query : "";
  const activePage = listPath === location ? page : 1;
  const pageSize = isSearchPage && !activeQuery ? 5 : 10;
  const [insurerId, setInsurerId] = useState<number | null>(null);
  const [providers, setProviders] = useState<Record<string, any>[]>([]);
  const [providerSearch, setProviderSearch] = useDebounceValue("", 1000);
  const [chase, setChase] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [pLoading, setPLoading] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<Record<string, any>>({});
  const [type, setType] = useState<"Sales" | "Renewals" | "all">("all");
  const [productType, setProductType] = useState<
    "Standard" | "Non-Standard" | "all"
  >("all");
  const searchQuery = new URLSearchParams(window.location.search).get("query");

  // ---------------------------------------- [ FIND CHASES ] -------------------------------
  useEffect(() => {
    const getChaseList = async () => {
      setLoading(true);
      const filtersArray = [
        { search: activeQuery },
        { status: chaseStatus },
        mycustomer && { customerExCode: currentUser?.code },
        customerArchive && { status: "Customer-Archives" },
        insurerId !== null && { insurerId: insurerId },
        dateRange?.startDate !== null && { startDate: dateRange?.startDate },
        dateRange?.endDate !== null && { endDate: dateRange?.endDate },
        type !== "all" && { chaseType: type },
        productType !== "all" && { policyType: productType },
      ];
      onChaseMenuClose();
      try {
        const res = await axiosInstance.get(
          `/chase?page=${activePage}&limit=${pageSize}${formQueryUrl(filtersArray)}`
        );
        setChase(res.data.data);
        setTotalCount(res.data.totalCount);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };

    !searchQuery && getChaseList();
  }, [
    isSearchPage,
    activeQuery,
    chaseStatus,
    activePage,
    pageSize,
    mycustomer,
    currentUser?.code,
    insurerId,
    dateRange,
    type,
    productType,
    customerArchive,
  ]);

  // -------------------------------- [ UPDATE CURRENT PAGE ] --------------------------

  const OnChangeCurrentPage = (page) => {
    setListPath(location);
    setPage(page);
  };

  // -------------------------------- [ SEARCH CUSTOMERS ] --------------------------------

  const HandleSearchCustomer = (value: string) => {
    setListPath(location);
    setPage(1);
    setQuery(value);
    setQuery.flush();
  };

  // -------------------------------- [ NAVIGATE TO USER ] --------------------------------

  const handleClick = () => {
    navigate("/createChase");
  };

  // -------------------------------- [ FIND PROVIDERS ] --------------------------------------

  useEffect(() => {
    const findProviders = async () => {
      setPLoading(true);
      try {
        const providerRes = await axiosInstance.get(
          providerSearch ? `/provider?search=${providerSearch}` : "/provider"
        );
        setProviders([
          { label: "All", name: null },
          ...providerRes.data.data?.map((providerItem) => ({
            name: providerItem.id,
            label: `${providerItem.name} (${providerItem.type} - ${providerItem?.productType}/${providerItem?.schemeCode})`,
          })),
        ]);
        setPLoading(false);
      } catch (error) {
        ParseErrors(error);
        setPLoading(false);
      }
    };

    findProviders();
  }, [providerSearch]);

  // ------------------------------- [ SEARCH BASED ON URL QUERY ] --------------------

  useEffect(() => {
    const updateQuery = () => {
      if (searchQuery && searchRef.current) {
        searchRef.current.value = searchQuery;
        setListPath(location);
        setQuery(searchQuery);
        navigate(window.location.pathname);
      }
    };

    currentUser?.id && !loading && updateQuery();
  }, [searchQuery, navigate, setQuery, searchRef, currentUser?.id, loading, location]);

  // --------------------------------- [ IF JUST ONE CHASE ITEM OPEN THAT UP ] -----------

  useEffect(() => {
    if (chase.length === 1) {
      const item = chase[0];
      onToggleChaseMenu({
        id: item?.id,
        chaseItems: item?.chaseItem,
        drivers: item?.drivers,
        data: {
          policyReferenceNumber: item?.policyReferenceNumber,
          apiValues: item?.apiValues,
        },
        timeline: item?.timeline,
      });
    }
  }, [chase]);

  // ------------------------------ [ ON PAGE CHANGE GO BACK TO PAGE 1 ] -----------------

  useEffect(() => {
    setPage(1);
    // the same component serves every list route, so a search must not follow the user to the next page
    if (previousLocation.current === location) return;
    previousLocation.current = location;
    if (searchRef.current) searchRef.current.value = "";
    setQuery("");
    setQuery.flush();
  }, [location, setQuery]);

  return (
    <PageLayout
      pageTitle={
        myCutomers
          ? window.location.pathname.replace("-", " ").replace("/", "")
          : "Dashboard"
      }
      primaryAction={
        <div className="flex items-center justify-end gap-2.5 flex-wrap smallTablet:justify-start smallTablet:w-full">
          <ChatIcon isCustomer={myCutomers} />

          <button
            onClick={handleClick}
            className="flex gap-2 text-[15px] font-dmSans font-[600] items-center h-10 px-5 smallTablet:ml-auto text-white bg-gradient-to-r from-accent to-accent-bright shadow-lg shadow-indigo-500/20 hover:shadow-glow-sm hover:-translate-y-0.5 transition-all duration-300 rounded-full"
          >
            <FontAwesomeIcon icon={faAdd} className="text-[13px]" />
            New Chase
          </button>
        </div>
      }
    >
      <ChaseFilterBar
        type={type}
        setType={setType}
        productType={productType}
        setProductType={setProductType}
        providers={providers}
        pLoading={pLoading}
        insurerId={insurerId}
        setInsurerId={setInsurerId}
        setProviderSearch={setProviderSearch}
        dateRange={dateRange}
        setDateRange={setDateRange}
      />

      {window.location.pathname === "/my-customers" && (
        <div>
          <TopBarWidget />
        </div>
      )}

      {isSearchPage && (
        <CustomerSearch
          inputRef={searchRef}
          hasSearch={!!activeQuery}
          onSearch={HandleSearchCustomer}
          onClear={() => HandleSearchCustomer("")}
        />
      )}

      <div className="mt-5 mr-1 smallTablet:mr-0 bg-surface border border-edge rounded-2xl shadow-card px-6 pt-6 pb-2 smallTablet:px-3 smallTablet:pt-4 font-medium animate-fade-in">
        <div className="flex w-full items-start justify-between gap-4 flex-wrap mb-5">
          <div className="min-w-0">
            <h1 className="text-[22px] font-bold text-text-primary leading-tight capitalize">
              {isSearchPage
                ? activeQuery
                  ? "Search results"
                  : "Latest chases"
                : "Chase list"}
            </h1>
            <p className="mt-1 text-[13px] font-medium text-light-gray capitalize">
              {isSearchPage ? (
                <span className="normal-case">
                  {activeQuery ? `“${activeQuery}”` : "Most recent first"}
                </span>
              ) : chaseStatus ? (
                chaseStatus.replace("-", " ")
              ) : (
                "All statuses"
              )}
              <span className="normal-case">
                {" "}
                · {new Intl.NumberFormat("en-GB").format(totalCount)} chases
              </span>
            </p>
          </div>
          {!isSearchPage && (
            <div className="h-10 min-w-[290px] smallTablet:min-w-0 smallTablet:w-full rounded-full flex bg-input-bg items-center border border-input-border focus-within:border-indigo-400/40 focus-within:ring-2 focus-within:ring-indigo-400/20 transition-all duration-200 px-4">
              <FontAwesomeIcon
                icon={faSearch}
                className="text-[13px] text-light-gray mr-2.5 shrink-0"
              />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search name, email or reference"
                className="flex-grow bg-inherit text-[13px] text-text-primary placeholder:text-light-gray font-medium focus:outline-none"
                onChange={(e) => {
                  setListPath(location);
                  setQuery(e.target.value);
                }}
              />
            </div>
          )}
        </div>

        <TableWrapper
          headingData={chaseHeadingData}
          isLoading={loading}
          totalCount={totalCount}
          headerClass={tableHeaderClass}
          additionalClasses="smallTablet:!min-w-[960px]"
        >
          {chase?.map((item, index) => {
            return <ChaseRenderRow item={item} index={index} key={item.id} />;
          })}
        </TableWrapper>
        <PaginationMenu
          totalCount={totalCount}
          currentPage={page}
          pageSize={pageSize}
          onPageChange={OnChangeCurrentPage}
        />
      </div>
    </PageLayout>
  );
};

export default ChaseList;
